import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Star,
  Trash2,
  X,
} from "lucide-react";
import "./CommandConversationNav.css";

function CommandConversationNav({
  currentConversationId,
  groups = [],
  searchQuery = "",
  onSearchChange,
  isLoading = false,
  error = null,
  onRetry,
  onSelectConversation,
  onNewConversation,
  onRenameConversation,
  onToggleStar,
  onDeleteConversation,
  onOpenConversationActions,
  onManageConversations,
  onClose,
  isOpen = true,
  isModal = false,
}) {
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuAreaRef = useRef(null);
  const navRef = useRef(null);
  const returnFocusRef = useRef(null);
  const groupIdPrefix = React.useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const normalizedGroups = useMemo(
    () =>
      (Array.isArray(groups) ? groups : []).map((group) => ({
        ...group,
        conversations: Array.isArray(group.conversations)
          ? group.conversations
          : [],
      })),
    [groups],
  );

  useEffect(() => {
    setExpandedGroups((previous) => {
      const next = new Set(previous);
      normalizedGroups.forEach((group) => {
        if (group.collapsible === false || group.initiallyExpanded) {
          next.add(group.id);
        }
      });
      return next;
    });
  }, [normalizedGroups]);

  useEffect(() => {
    if (!openMenuId) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!menuAreaRef.current?.contains(event.target)) setOpenMenuId(null);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        event.stopImmediatePropagation();
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape, true);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape, true);
    };
  }, [openMenuId]);

  useEffect(() => {
    if (!isOpen || !isModal) return undefined;
    returnFocusRef.current = document.activeElement;
    const nav = navRef.current;
    nav?.focus();

    const containFocus = (event) => {
      if (event.key === "Escape" && !openMenuId) {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== "Tab" || !nav) return;
      const focusable = [...nav.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      )];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    nav.addEventListener("keydown", containFocus);
    return () => {
      nav.removeEventListener("keydown", containFocus);
      returnFocusRef.current?.focus?.();
    };
  }, [isOpen, isModal, openMenuId]);

  const conversationCount = normalizedGroups.reduce(
    (count, group) => count + group.conversations.length,
    0,
  );
  const hasQuery = searchQuery.trim().length > 0;

  const toggleGroup = (groupId) => {
    setExpandedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const runAction = (callback, conversation) => {
    setOpenMenuId(null);
    callback?.(conversation);
  };

  const renderConversation = (conversation) => {
    const isCurrent = conversation.id === currentConversationId;
    const hasBuiltInActions =
      onRenameConversation || onToggleStar || onDeleteConversation;
    const hasActions = onOpenConversationActions || hasBuiltInActions;

    return (
      <li key={conversation.id} className="command-conversation-nav__item">
        <button
          type="button"
          className={`command-conversation-nav__conversation ${isCurrent ? "is-current" : ""}`}
          onClick={() => onSelectConversation?.(conversation.id)}
          aria-current={isCurrent ? "page" : undefined}
        >
          <span className="command-conversation-nav__title">
            {conversation.isStarred && (
              <>
                <Star size={12} fill="currentColor" aria-hidden="true" />
                <span className="sr-only">Starred</span>
              </>
            )}
            <span>{conversation.title || "Untitled command"}</span>
          </span>
          {conversation.updatedAtLabel && (
            <span className="command-conversation-nav__time">
              {conversation.updatedAtLabel}
            </span>
          )}
        </button>

        {hasActions && (
          <div
            className="command-conversation-nav__actions"
            ref={openMenuId === conversation.id ? menuAreaRef : undefined}
          >
            <button
              type="button"
              className="command-conversation-nav__more"
              aria-label={`Actions for ${conversation.title || "untitled command"}`}
              aria-haspopup="menu"
              aria-expanded={openMenuId === conversation.id}
              onClick={() => {
                if (onOpenConversationActions) {
                  onOpenConversationActions(conversation);
                } else {
                  setOpenMenuId((current) =>
                    current === conversation.id ? null : conversation.id,
                  );
                }
              }}
            >
              <MoreHorizontal size={16} aria-hidden="true" />
            </button>

            {!onOpenConversationActions && openMenuId === conversation.id && (
              <div className="command-conversation-nav__menu">
                {onRenameConversation && (
                  <button
                    type="button"
                    onClick={() => runAction(onRenameConversation, conversation)}
                  >
                    <Pencil size={14} aria-hidden="true" /> Rename
                  </button>
                )}
                {onToggleStar && (
                  <button
                    type="button"
                    onClick={() => runAction(onToggleStar, conversation)}
                  >
                    <Star size={14} aria-hidden="true" />
                    {conversation.isStarred ? "Remove star" : "Star"}
                  </button>
                )}
                {onDeleteConversation && (
                  <button
                    type="button"
                    className="is-danger"
                    onClick={() => runAction(onDeleteConversation, conversation)}
                  >
                    <Trash2 size={14} aria-hidden="true" /> Delete
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <nav
      ref={navRef}
      className="command-conversation-nav"
      aria-label="Command conversations"
      role={isModal ? "dialog" : undefined}
      aria-modal={isModal ? "true" : undefined}
      tabIndex={isModal ? -1 : undefined}
    >
      <div className="command-conversation-nav__header">
        <div>
          <span className="command-conversation-nav__eyebrow">Workspace</span>
          <h2>Commands</h2>
        </div>
        <div className="command-conversation-nav__header-actions">
          {onClose && (
            <button
              type="button"
              className="command-conversation-nav__close"
              onClick={onClose}
              aria-label="Close command conversations"
            >
              <X size={17} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            className="command-conversation-nav__new"
            onClick={onNewConversation}
            aria-label="Start a new command"
            title="New command"
          >
            <Plus size={17} aria-hidden="true" />
          </button>
        </div>
      </div>

      <label className="command-conversation-nav__search">
        <Search size={15} aria-hidden="true" />
        <span className="sr-only">Search commands</span>
        <input
          type="search"
          value={searchQuery}
          onChange={(event) => onSearchChange?.(event.target.value)}
          placeholder="Find a command"
        />
        {hasQuery && (
          <button
            type="button"
            aria-label="Clear command search"
            onClick={() => onSearchChange?.("")}
          >
            <X size={14} aria-hidden="true" />
          </button>
        )}
      </label>

      <div
        className="command-conversation-nav__body"
        aria-busy={isLoading}
      >
        {isLoading && conversationCount === 0 && (
          <div className="command-conversation-nav__skeleton" role="status">
            <span className="sr-only">Loading commands</span>
            {[0, 1, 2, 3].map((item) => (
              <span key={item} aria-hidden="true" />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div className="command-conversation-nav__state" role="alert">
            <strong>Commands couldn’t load</strong>
            <p>{typeof error === "string" ? error : "Try loading them again."}</p>
            {onRetry && (
              <button type="button" onClick={onRetry}>
                <RefreshCw size={14} aria-hidden="true" /> Retry
              </button>
            )}
          </div>
        )}

        {!error && !isLoading && conversationCount === 0 && (
          <div className="command-conversation-nav__state" role="status">
            <strong>{hasQuery ? "No matching commands" : "No commands yet"}</strong>
            <p>
              {hasQuery
                ? "Try another title or keyword."
                : "Start with a business outcome for Ollie."}
            </p>
            {!hasQuery && (
              <button type="button" onClick={onNewConversation}>
                <Plus size={14} aria-hidden="true" /> New command
              </button>
            )}
          </div>
        )}

        {!error && conversationCount > 0 && (
          <div className="command-conversation-nav__groups">
            {normalizedGroups.map((group) => {
              if (group.conversations.length === 0) return null;
              const collapsible = group.collapsible !== false;
              const isExpanded = !collapsible || expandedGroups.has(group.id);
              const listId = `${groupIdPrefix}-${group.id}`;

              return (
                <section key={group.id} aria-labelledby={`${listId}-label`}>
                  {collapsible ? (
                    <button
                      type="button"
                      id={`${listId}-label`}
                      className="command-conversation-nav__group-heading"
                      aria-expanded={isExpanded}
                      aria-controls={listId}
                      onClick={() => toggleGroup(group.id)}
                    >
                      {isExpanded ? (
                        <ChevronDown size={14} aria-hidden="true" />
                      ) : (
                        <ChevronRight size={14} aria-hidden="true" />
                      )}
                      <span>{group.label}</span>
                      <small>{group.conversations.length}</small>
                    </button>
                  ) : (
                    <h3 id={`${listId}-label`} className="command-conversation-nav__group-label">
                      {group.label}
                      <small>{group.conversations.length}</small>
                    </h3>
                  )}
                  {isExpanded && (
                    <ul id={listId}>{group.conversations.map(renderConversation)}</ul>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
      {onManageConversations && (
        <div className="command-conversation-nav__footer">
          <button type="button" onClick={onManageConversations}>
            Manage conversations
          </button>
        </div>
      )}
    </nav>
  );
}

export default CommandConversationNav;
